import React, { useState, useEffect } from 'react';
import CustomFieldsService from '../../../AppSettings/services/CustomfieldsService';
import './CustomField.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTimes,
  faEdit,
  faTrash,
  faSearch,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import CreateModal from '../../../Components/CreateModal/CreateModal';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';

const CustomFields = () => {
  const [customFields, setCustomFields] = useState([]);
  const [groupRules, setGroupRules] = useState([]);
  const [error, setError] = useState(null);
  const [newCustomField, setNewCustomField] = useState({
    custom_field: '',
    employeeid: '',
    type: 'text',
  });
  const [editingField, setEditingField] = useState(null);
  const [employee, setEmployee] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFields, setSelectedFields] = useState({});
  const [newGroupName, setNewGroupName] = useState('');
  const [isGroupsExpanded, setIsGroupsExpanded] = useState(false);
  const employeeId = useActiveEmployeeId();

  useEffect(() => {
    fetchCustomFields();
    fetchGroupRules();
  }, []);

  useEffect(() => {
    if (employeeId) {
      fetchEmployee(employeeId);
    }
  }, [employeeId]);

  // Check if any fields are selected
  const hasSelectedFields = Object.values(selectedFields).some(
    (value) => value
  );

  // Filter custom fields based on search query
  const filteredFields = customFields.filter((field) =>
    field.custom_field.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fetchEmployee = async (currentEmployeeId) => {
    try {
      const employeeData = await EmployeeService.getEmployeeById(
        currentEmployeeId
      );
      setEmployee(employeeData);
      setNewCustomField((prev) => ({
        ...prev,
        employeeid: employeeData.employeeid,
      }));
    } catch (error) {
      console.error('Error fetching employee data:', error);
    }
  };

  const fetchGroupRules = async () => {
    try {
      const response = await CustomFieldsService.getGroupRules();
      // Same envelope shape as getCustomFields — /group-rules is wrapped by create_success_response
      // ({ data: GroupRule[], … }) despite its zod type claiming a bare array. Unwrap so
      // `groupRules.map` (Field Groups expand) can't crash the page.
      const rules = Array.isArray(response) ? response : (response?.data ?? []);
      setGroupRules(rules);
    } catch (err) {
      console.error('Error fetching group rules:', err);
      setGroupRules([]); // Ensure it's always an array
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await CustomFieldsService.getCustomFields();
      // getCustomFields resolves the response envelope { data: CustomField[], ... }, not a bare
      // array — storing the envelope made `customFields.filter` throw and white-screened the whole
      // Profile page. Unwrap to the array (mirrors useTableData.js normalization); a bare array
      // (older response shape) is still accepted.
      const fields = Array.isArray(response) ? response : (response?.data ?? []);
      setCustomFields(fields);
      setError(null);
    } catch (err) {
      setError('An error occurred while fetching custom fields.');
      console.error(err);
      setCustomFields([]); // Ensure it's always an array
    }
  };

  const handleCreateField = async (e) => {
    e.preventDefault();
    try {
      if (!employee.employeeid) {
        throw new Error('Employee ID not available');
      }

      const fieldData = {
        custom_field: newCustomField.custom_field,
        employeeid: employee.employeeid,
      };
      await CustomFieldsService.createCustomField(fieldData);
      setNewCustomField({ custom_field: '', employeeid: employee.employeeid });
      setIsModalOpen(false);
      fetchCustomFields();
      setError(null);
    } catch (err) {
      setError('An error occurred while creating the custom field.');
      console.error(err);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      const selectedFieldIds = Object.entries(selectedFields)
        .filter(([_, isSelected]) => isSelected)
        .map(([id, _]) => parseInt(id));

      if (selectedFieldIds.length === 0) {
        throw new Error('No fields selected');
      }

      const groupData = {
        group_name: newGroupName,
        assoc_customfields: selectedFieldIds.reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {}),
      };

      await CustomFieldsService.createGroupRule(groupData);
      setNewGroupName('');
      setSelectedFields({});
      setIsGroupModalOpen(false);
      setError(null);
    } catch (err) {
      setError('An error occurred while creating the group.');
      console.error(err);
    }
  };

  const handleUpdateField = async (customFieldId) => {
    try {
      await CustomFieldsService.updateCustomField(customFieldId, editingField);
      setEditingField(null);
      fetchCustomFields();
      setError(null);
    } catch (err) {
      setError('An error occurred while updating the custom field.');
      console.error(err);
    }
  };

  const handleDeleteField = async (customFieldId) => {
    if (window.confirm('Are you sure you want to delete this custom field?')) {
      try {
        await CustomFieldsService.deleteCustomField(customFieldId);
        fetchCustomFields();
        setError(null);
      } catch (err) {
        setError('An error occurred while deleting the custom field.');
        console.error(err);
      }
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        await CustomFieldsService.deleteGroupRule(groupId);
        fetchGroupRules();
        setError(null);
      } catch (err) {
        setError('An error occurred while deleting the group.');
        console.error(err);
      }
    }
  };

  const toggleFieldSelection = (fieldId) => {
    setSelectedFields((prev) => ({
      ...prev,
      [fieldId]: !prev[fieldId],
    }));
  };

  return (
    <div className="custom-fields-container">
      <div className="custom-fields-header">
        <h1 className="custom-fields-title">Custom Fields</h1>
        <div className="header-actions">
          <div className="search-bar">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search custom fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button
            className="add-field-button"
            onClick={() => setIsModalOpen(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      {/* Group Action Button - Only shows when fields are selected */}
      {hasSelectedFields && (
        <div className="group-action-bar">
          <button
            className="group-selected-button"
            onClick={() => setIsGroupModalOpen(true)}
          >
            <FontAwesomeIcon icon={faChevronDown} /> Group Selected Fields
          </button>
          <button
            className="clear-selection-button"
            onClick={() => setSelectedFields({})}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Modal for Creating New Custom Field - Redesigned for intuitive UX */}
      <CreateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create Custom Field"
        onSubmit={handleCreateField}
        submitButtonText={newCustomField.custom_field.trim() ? 'Create Field' : 'Enter a name to continue'}
      >
        <div style={{ padding: '8px 0' }}>
          {/* Step indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              background: newCustomField.custom_field.trim() ? '#4338ca' : '#e5e7eb',
              color: newCustomField.custom_field.trim() ? '#fff' : '#9ca3af',
              fontSize: 12,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}>1</span>
            <label style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
              Name your custom field
            </label>
          </div>

          {/* Helper text */}
          <p style={{ 
            margin: '0 0 16px 36px', 
            fontSize: 13, 
            color: '#6b7280', 
            lineHeight: 1.5 
          }}>
            Custom fields let you capture additional information specific to your workflow. 
            They appear as form fields when creating or editing matters.
          </p>

          {/* Input field */}
          <div style={{ marginLeft: 36, marginBottom: 16 }}>
            <input
              type="text"
              placeholder="e.g., Case Priority, Court Jurisdiction, Filing Deadline..."
              value={newCustomField.custom_field}
              onChange={(e) =>
                setNewCustomField({
                  ...newCustomField,
                  custom_field: e.target.value,
                })
              }
              required
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: 15,
                border: '1.5px solid #e2e8f0',
                borderRadius: 10,
                background: '#f8fafc',
                transition: 'all 0.2s ease',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#4338ca';
                e.target.style.background = '#fff';
                e.target.style.boxShadow = '0 0 0 3px rgba(67, 56, 202, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.background = '#f8fafc';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Preview section - shows when name is entered */}
          {newCustomField.custom_field.trim() && (
            <div style={{ marginLeft: 36, marginBottom: 16 }}>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, fontWeight: 500 }}>
                Preview: How it will appear on forms
              </p>
              <div style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: 16,
              }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: 13, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 6 
                }}>
                  {newCustomField.custom_field}
                </label>
                <div style={{
                  width: '100%',
                  height: 40,
                  background: '#f9fafb',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: 12,
                  color: '#9ca3af',
                  fontSize: 14,
                }}>
                  Enter value...
                </div>
              </div>
            </div>
          )}

          {/* Tip box - shows when field is empty */}
          {!newCustomField.custom_field.trim() && (
            <div style={{
              marginLeft: 36,
              padding: '14px 16px',
              background: '#fef3c7',
              borderRadius: 10,
              border: '1px solid #fcd34d',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}>
              <span style={{ fontSize: 16 }}>💡</span>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                  Need ideas?
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#a16207', lineHeight: 1.5 }}>
                  Common custom fields: <strong>Case Number</strong>, <strong>Opposing Counsel</strong>, 
                  <strong> Court Name</strong>, <strong>Settlement Amount</strong>, <strong>Statute of Limitations</strong>
                </p>
              </div>
            </div>
          )}

          {/* Existing fields info */}
          {customFields.length > 0 && (
            <div style={{
              marginLeft: 36,
              marginTop: 16,
              padding: '12px 14px',
              background: '#f0f9ff',
              borderRadius: 10,
              border: '1px solid #bae6fd',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <span style={{ fontSize: 14 }}>📋</span>
              <span style={{ fontSize: 12, color: '#0369a1' }}>
                You currently have <strong>{customFields.length}</strong> custom field{customFields.length !== 1 ? 's' : ''} defined
              </span>
            </div>
          )}
        </div>
      </CreateModal>

      {/* Modal for Creating New Group */}
      {isGroupModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Create New Group</h2>
              <button
                className="close-modal"
                onClick={() => setIsGroupModalOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleCreateGroup}>
              <input
                type="text"
                placeholder="Group Name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                required
              />
              <div className="selected-fields-preview">
                <h3>Selected Fields:</h3>
                <ul>
                  {(customFields || [])
                    .filter((field) => selectedFields[field.customfield_id])
                    .map((field) => (
                      <li key={field.customfield_id}>{field.custom_field}</li>
                    ))}
                </ul>
              </div>
              <button type="submit">Create Group</button>
            </form>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && <p className="error-message">{error}</p>}

      {/* Custom Fields List */}
      <div className="custom-fields-list">
        {filteredFields.length === 0 ? (
          <p className="no-fields-message">
            {searchQuery
              ? 'No matching fields found.'
              : 'No custom fields available.'}
          </p>
        ) : (
          <ul>
            {filteredFields.map((field) => (
              <li key={field.customfield_id} className="custom-field-item">
                <div className="field-checkbox">
                  <input
                    type="checkbox"
                    checked={!!selectedFields[field.customfield_id]}
                    onChange={() => toggleFieldSelection(field.customfield_id)}
                  />
                </div>
                {editingField &&
                editingField.customfield_id === field.customfield_id ? (
                  <div className="edit-field-form">
                    <input
                      type="text"
                      value={editingField.custom_field}
                      onChange={(e) =>
                        setEditingField({
                          ...editingField,
                          custom_field: e.target.value,
                        })
                      }
                    />
                    <div className="edit-actions">
                      <button
                        onClick={() => handleUpdateField(field.customfield_id)}
                      >
                        Save
                      </button>
                      <button onClick={() => setEditingField(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="field-content">
                    <p>{field.custom_field}</p>
                    <div className="field-actions">
                      <button onClick={() => setEditingField(field)}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleDeleteField(field.customfield_id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Groups Section */}
      <div className="groups-section">
        <button
          className="groups-toggle-button"
          onClick={() => setIsGroupsExpanded(!isGroupsExpanded)}
        >
          <FontAwesomeIcon
            icon={isGroupsExpanded ? faChevronUp : faChevronDown}
          />
          Field Groups ({groupRules.length})
        </button>

        {isGroupsExpanded && (
          <div className="groups-list">
            {groupRules.length === 0 ? (
              <p className="no-groups-message">No groups available.</p>
            ) : (
              <ul>
                {groupRules.map((group) => (
                  <li key={group.id} className="group-item">
                    <div className="group-header">
                      <h3>{group.group_name}</h3>
                      <button
                        className="delete-group-button"
                        onClick={() => handleDeleteGroup(group.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    <div className="group-fields">
                      {Object.keys(group.assoc_customfields).map((fieldId) => {
                        const field = (customFields || []).find(
                          (f) => f.customfield_id === parseInt(fieldId)
                        );
                        return field ? (
                          <span key={fieldId} className="group-field-tag">
                            {field.custom_field}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFields;
